import { PayloadAction, createReducer } from "@reduxjs/toolkit";
import { WritableDraft } from "immer";
import { DateTime } from "luxon";
import { IApiSelectOption } from "../../components/application/advancedsearch/AdvancedSearchElement";
import type {
    CreateAccountResponse,
    GetOrganizationsResponse,
    LicenseResponse,
    OrganizationLicenseResponse,
    OrganizationLicensesResponse,
    OrganizationResponse,
    OrganizationUserResponse,
    OrganizationUsersResponse,
    OrganizationsBillingCustomerResponse,
    OrganizationsBillingCustomersResponse,
    OrganizationsFeaturesResponse,
} from "../../data/models/schemas";
import {
    closeCreateAccountResult,
    createAccount,
    getFeatures,
    getLicenses,
    getOrganization,
    getOrganizationBillingCustomers,
    getOrganizationFeatures,
    getOrganizationLicenses,
    getOrganizations,
    getOrganizationUsers,
    saveOrganizationLicense,
    updateOrganization,
    updateOrganizationFeatures,
} from "../actions/viridischemadmin";

export type IOrganization = OrganizationResponse;

export type ILicense = LicenseResponse;

export type IBillingCustomer = OrganizationsBillingCustomerResponse;

export type IOrganizationReduxState = {
    organization?: IOrganization;
    features?: string[];
    license_features?: string[];
    effective_features?: string[];
    organization_all_features?: boolean;
    license_all_features?: boolean;
    licenses?: OrganizationLicenseResponse[];
    users?: OrganizationUserResponse[];
    billing_customers?: IBillingCustomer[];
};

export type IOrganizationsReduxState = {
    organizations: IOrganization[];
    organizations_data: { [key: number]: IOrganizationReduxState };
    pages: number;
};

export type IViridisChemAdminReduxState = {
    organizations: IOrganizationsReduxState;
    features: IApiSelectOption[];
    licenses: ILicense[];
    saved_timestamp: number;
    createAccountResult?: CreateAccountResponse;
};

const getDefaultState = (): IViridisChemAdminReduxState => {
    return {
        organizations: {
            organizations: [],
            organizations_data: {},
            pages: 1,
        },
        features: [],
        licenses: [],
        saved_timestamp: 0,
    };
};

const createIfNotExists = (
    state: WritableDraft<IViridisChemAdminReduxState>,
    organizationId: number
) => {
    if (!state.organizations.organizations_data[organizationId]) {
        state.organizations.organizations_data[organizationId] = {};
    }

    const orgState = state.organizations.organizations_data[organizationId];

    if (!orgState.organization) {
        orgState.organization = {
            id: organizationId,
        };
    }

    return orgState;
};

export const viridischemadmin = createReducer(getDefaultState(), (builder) => {
    builder.addCase(
        getFeatures.success,
        (state, action: PayloadAction<IApiSelectOption[]>) => {
            state.features = action.payload;
        }
    );
    builder.addCase(
        getLicenses.success,
        (state, action: PayloadAction<LicenseResponse[]>) => {
            state.licenses = action.payload;
        }
    );
    builder.addCase(
        getOrganizations.success,
        (state, action: PayloadAction<GetOrganizationsResponse>) => {
            state.organizations.organizations =
                action.payload.organizations || [];
            state.organizations.pages = action.payload.pages || 1;
        }
    );
    builder.addCase(
        getOrganization.success,
        (state, action: PayloadAction<OrganizationResponse>) => {
            const organizationId = action.payload.id;
            if (organizationId == null) {
                return;
            }
            const orgState = createIfNotExists(state, organizationId);
            orgState.organization = action.payload;
        }
    );

    builder.addCase(
        updateOrganization.success,
        (state, action: PayloadAction<OrganizationResponse>) => {
            const organizationId = action.payload.id;
            if (organizationId == null) {
                return;
            }
            const orgState = createIfNotExists(state, organizationId);
            orgState.organization = action.payload;
            const listIndex = state.organizations.organizations.findIndex(
                (organization) => organization.id === organizationId
            );
            if (listIndex >= 0) {
                state.organizations.organizations[listIndex] = action.payload;
            }
            state.saved_timestamp = DateTime.now().toSeconds();
        }
    );

    builder.addCase(
        getOrganizationLicenses.success,
        (state, action: PayloadAction<OrganizationLicensesResponse>) => {
            const organizationId = action.payload.organization_id;
            if (organizationId == null) {
                return;
            }
            const orgState = createIfNotExists(state, organizationId);
            orgState.licenses = action.payload.licenses || [];
        }
    );

    builder.addCase(
        saveOrganizationLicense.success,
        (state, action: PayloadAction<OrganizationLicenseResponse>) => {
            const organizationId = action.payload.organization_id;
            if (organizationId == null) {
                return;
            }
            const orgState = createIfNotExists(state, organizationId);
            if (orgState.licenses) {
                const index = orgState.licenses.findIndex(
                    (license) => license.id === action.payload.id
                );
                if (index >= 0) {
                    orgState.licenses[index] = action.payload;
                } else {
                    orgState.licenses.push(action.payload);
                }
            }
            orgState.organization = undefined;
            orgState.license_features = undefined;
            orgState.effective_features = undefined;
            orgState.license_all_features = undefined;
            state.saved_timestamp = DateTime.now().toSeconds();
        }
    );

    builder.addCase(
        getOrganizationUsers.success,
        (state, action: PayloadAction<OrganizationUsersResponse>) => {
            const organizationId = action.payload.organization_id;
            if (organizationId == null) {
                return;
            }
            const orgState = createIfNotExists(state, organizationId);
            orgState.users = action.payload.users || [];
        }
    );

    builder.addCase(
        getOrganizationFeatures.success,
        (state, action: PayloadAction<OrganizationsFeaturesResponse>) => {
            const organizationId = action.payload.organization_id;
            if (organizationId == null) {
                return;
            }
            const orgState = createIfNotExists(state, organizationId);
            orgState.features = action.payload.features || [];
            orgState.license_features = action.payload.license_features || [];
            orgState.effective_features =
                action.payload.effective_features || [];
            orgState.organization_all_features =
                action.payload.organization_all_features === true;
            orgState.license_all_features =
                action.payload.license_all_features === true;
        }
    );

    builder.addCase(
        updateOrganizationFeatures.success,
        (state, action: PayloadAction<OrganizationsFeaturesResponse>) => {
            const organizationId = action.payload.organization_id;
            if (organizationId == null) {
                return;
            }
            const orgState = createIfNotExists(state, organizationId);
            orgState.features = action.payload.features || [];
            orgState.license_features = action.payload.license_features || [];
            orgState.effective_features =
                action.payload.effective_features || [];
            orgState.organization_all_features =
                action.payload.organization_all_features === true;
            orgState.license_all_features =
                action.payload.license_all_features === true;
            state.saved_timestamp = DateTime.now().toSeconds();
        }
    );
    builder.addCase(
        getOrganizationBillingCustomers.success,
        (
            state,
            action: PayloadAction<OrganizationsBillingCustomersResponse>
        ) => {
            const organizationId = action.payload.organization_id;
            if (organizationId == null) {
                return;
            }
            const orgState = createIfNotExists(state, organizationId);
            orgState.billing_customers = action.payload.billing_customers || [];
        }
    );
    builder.addCase(
        createAccount.success,
        (state, action: PayloadAction<CreateAccountResponse>) => {
            state.saved_timestamp = DateTime.now().toSeconds();
            state.createAccountResult = action.payload;
        }
    );

    builder.addCase(closeCreateAccountResult, (state) => {
        state.createAccountResult = undefined;
    });
});
